import moment from 'moment-timezone';

import { get } from '../storage';

export { default as getLocalBoundsFromUtcRangeSeconds } from './getLocalBoundsFromUtcRangeSeconds';
export { getActiveRecordsWindowStart } from './activeRecordsWindow';
export {
  expandDaysOfWeekForFirestoreQuery,
  formatDaysOfWeek,
  LEGACY_SUNDAY_DAY_INDEX,
  normalizeDaysOfWeek,
  normalizeDaysOfWeekValue,
} from './formatDaysOfWeek';
export {
  clampCalendarRangeToToday,
  disableCalendarDatesAfterToday,
  isCalendarDayAfterToday,
} from './disableFutureCalendarDates';

const DEFAULT_TIMEZONE = get('timeZone', moment.tz.guess(true)) || 'UTC';
export const getDefaultTimeZone = () => DEFAULT_TIMEZONE;
export const setDefaultTimeZone = tz => moment.tz.setDefault(tz);
export const AUDIT_DATE_FORMAT = 'DD/MM/YYYY HH:mm';

export class DateUtil {
  static DEFAULT_DT_DATE_FORMAT = 'DD/MM/YY';
  static DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';
  static DEFAULT_TIME_FORMAT = 'HH:mm';
  static DEFAULT_TIME_WITH_SECONDS_FORMAT = 'HH:mm:ss';
  static AUDIT_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

  /**
   * @description Convert a timestamp, date or string to a date string
   * @param {number|Date|string} date - The timestamp, date or string to convert
   * @param {string} format - The format to convert the date to
   * @param {string} timezone - The timezone to convert the date to
   * @returns {string} The date as string in the format provided (default: YYYY-MM-DD)
   */
  static toDateString(date: any, format = this.DEFAULT_DATE_FORMAT, timezone = null) {
    if (!date) return '-';

    // Firestore timestamp
    if (date.seconds !== undefined && date.nanoseconds !== undefined) {
      if (timezone) {
        const dateObj = this.toDate(date.seconds, date.nanoseconds);
        return moment.tz(dateObj, timezone).format(format);
      }
      return this.convertDateToString(date.seconds, date.nanoseconds, { format });
    }

    // Firestore timestamp like object
    if (date._seconds !== undefined && date._nanoseconds !== undefined) {
      if (timezone) {
        const dateObj = this.toDate(date._seconds, date._nanoseconds);
        return moment.tz(dateObj, timezone).format(format);
      }
      return this.convertDateToString(date._seconds, date._nanoseconds, { format });
    }

    // Date object or ISO string. Honor timezone the same way numeric millis do.
    if (date instanceof Date || typeof date === 'string') {
      if (timezone) {
        return moment.tz(date, timezone).format(format);
      }
      return moment(date).format(format);
    }

    // Number (timestamp in milliseconds)
    if (typeof date === 'number') {
      if (timezone) {
        return moment.tz(date, timezone).format(format);
      }
      return moment(date).format(format);
    }

    return '-';
  }

  /**
   * @description Format any timestamp-like value as a time string. Robust to the
   * serialized `{ _seconds }` shape (with or without `_nanoseconds`) that Elastic
   * documents carry, unlike {@link toDateString} which needs both fields present.
   * @param {Object|number|Date|string} value - Timestamp-like value
   * @param {string} format - moment time format (default: HH:mm)
   * @param {string} timezone - Optional timezone
   * @returns {string} The formatted time, or '-' when missing/invalid
   */
  static toTimeString(value, format = this.DEFAULT_TIME_FORMAT, timezone = null) {
    const ms = this.getTimestampMs(value);
    if (ms === null) return '-';
    return this.toDateString(ms, format, timezone);
  }

  static getDefaultTimeZone() {
    return get('timeZone', moment.tz.guess(true)) || 'UTC';
  }

  /**
   * @description Start/end-of-day bounds (in millis) for a reference instant, computed in a
   * given timezone. Used to build "today" windows for range queries on absolute timestamps.
   * @param {number} reference - Reference instant in millis (default: now)
   * @param {string} timezone - IANA timezone (default: app default timezone)
   * @returns {{ startMillis: number, endMillis: number }} Day bounds in millis
   */
  static getDayBoundsMillis(reference = Date.now(), timezone = this.getDefaultTimeZone()) {
    return {
      startMillis: moment.tz(reference, timezone).startOf('day').valueOf(),
      endMillis: moment.tz(reference, timezone).endOf('day').valueOf(),
    };
  }

  /**
   * @description Inclusive calendar-month bounds (in millis) for `month`/`year` in a timezone.
   * @param {number} month - 1-based month (1 = January)
   * @param {number} year - Full year
   * @param {string} timezone - IANA timezone (default: app default timezone)
   * @returns {{ startMillis: number, endMillis: number }} Month bounds in millis
   */
  static getMonthBoundsMillis(month, year, timezone = this.getDefaultTimeZone()) {
    const start = moment.tz({ year, month: month - 1, day: 1 }, timezone).startOf('day');
    return {
      startMillis: start.valueOf(),
      endMillis: start.clone().endOf('month').valueOf(),
    };
  }

  /**
   * @description Inclusive calendar-day bounds (in millis) for a `YYYY-MM-DD` date in a timezone.
   * @param {string} isoDate - Calendar date `YYYY-MM-DD`
   * @param {string} timezone - IANA timezone (default: app default timezone)
   * @returns {{ startMillis: number, endMillis: number }|null} Day bounds, or null if invalid
   */
  static getCalendarDateBoundsMillis(isoDate, timezone = this.getDefaultTimeZone()) {
    const day = moment.tz(isoDate, 'YYYY-MM-DD', true, timezone);
    if (!day.isValid()) return null;

    return {
      startMillis: day.clone().startOf('day').valueOf(),
      endMillis: day.clone().endOf('day').valueOf(),
    };
  }

  static setDefaultTimeZone(tz) {
    moment.tz.setDefault(tz);
  }

  /**
   * @description Get the milliseconds from a date value
   * @param {Date|string|number} value - The date value to get the milliseconds from
   * @returns {number} The milliseconds from the date value
   */
  static getMilliseconds(value) {
    if (!value) return null;
    if (value instanceof Date) return value.getTime();
    if (typeof value.toMillis === 'function') return value.toMillis();
    return value;
  }

  /**
   * @description Get milliseconds from any timestamp-like value.
   * Tolerant on purpose: schedules can carry their start/end as a Firestore
   * `Timestamp`, a serialized `{ _seconds }` / `{ seconds }` map, a millis number,
   * a `Date`, or an ISO string depending on how the document was written. A reader
   * that only knows `_seconds` turns the other shapes into `null -> Invalid Date -> "-"`.
   * @param {Object|number|Date|string} timestamp - Timestamp-like value
   * @returns {number|null} Milliseconds or null if missing/invalid
   */
  static getTimestampMs(timestamp) {
    if (timestamp === null || timestamp === undefined || timestamp === '') return null;

    // Already-millis number, or numeric string.
    if (typeof timestamp === 'number') return Number.isFinite(timestamp) ? timestamp : null;

    if (timestamp instanceof Date) {
      const ms = timestamp.getTime();
      return Number.isNaN(ms) ? null : ms;
    }

    if (typeof timestamp === 'string') {
      const ms = new Date(timestamp).getTime();
      return Number.isNaN(ms) ? null : ms;
    }

    if (typeof timestamp.toMillis === 'function') return timestamp.toMillis();

    if (typeof timestamp.toDate === 'function') {
      const parsed = timestamp.toDate();
      if (parsed instanceof Date) {
        const ms = parsed.getTime();
        return Number.isNaN(ms) ? null : ms;
      }
    }

    // Serialized Firestore shapes: `{ _seconds, _nanoseconds }` (elastic) or `{ seconds, nanoseconds }`.
    if (timestamp._seconds !== undefined) {
      return timestamp._seconds * 1000 + (timestamp._nanoseconds || 0) / 1e6;
    }
    if (timestamp.seconds !== undefined) {
      return timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1e6;
    }

    return null;
  }

  /**
   * @description Convert a timestamp to a date
   * @param {number} seconds - The seconds to convert
   * @param {number} nanoseconds - The nanoseconds to convert
   * @returns {Date} The date
   */
  static toDate(seconds = 0, nanoseconds = 0) {
    const milliseconds = nanoseconds / 1e6;
    return new Date(seconds * 1000 + milliseconds);
  }

  /**
   * @description Normalize Firestore timestamps, plain objects, strings, or numbers to a Date.
   * @param {unknown} value - Timestamp-like value from Firestore or APIs
   * @returns {Date|null} Parsed date or null when missing/invalid
   */
  static toDateOrNull(value) {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'object') {
      if (typeof value.toDate === 'function') {
        const parsed = value.toDate();
        return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
      }

      const seconds = value.seconds ?? value._seconds;
      const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0;
      if (seconds !== undefined && seconds !== null) {
        const parsed = this.toDate(Number(seconds), Number(nanoseconds) || 0);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }

      const ms = this.getTimestampMs(value);
      if (ms !== null) {
        const parsed = new Date(ms);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  }

  /**
   * @description Format date and time using Intl API respecting the locale
   * @param {Date|string|number} date - The date to format
   * @param {Object} intl - The react-intl intl object
   * @param {Object} dateOptions - Intl.DateTimeFormat options for date
   * @param {Object} timeOptions - Intl.DateTimeFormat options for time
   * @returns {{ date: string, time: string }} Object with formatted date and time
   */
  static formatDateTimeWithIntl(date, intl, dateOptions = {}, timeOptions = {}) {
    if (!date || !intl) return { date: '', time: '' };

    return {
      date: this.formatDateWithIntl(date, intl, dateOptions),
      time: this.formatTimeWithIntl(date, intl, timeOptions),
    };
  }

  /**
   * @description Format a time using Intl API respecting the locale
   * @param {Date|string|number} date - The date to format
   * @param {Object} intl - The react-intl intl object
   * @param {Object} options - Intl.DateTimeFormat options
   * @returns {string} The formatted time
   */
  static formatTimeWithIntl(date, intl, options = {}) {
    if (!date || !intl) return '';

    const defaultOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      ...options,
    };

    return intl.formatTime(date, defaultOptions);
  }

  /**
   * @description Format a date using Intl API respecting the locale
   * @param {Date|string|number} date - The date to format
   * @param {Object} intl - The react-intl intl object
   * @param {Object} options - Intl.DateTimeFormat options
   * @returns {string} The formatted date
   */
  static formatDateWithIntl(date, intl, options = {}) {
    if (!date || !intl) return '';

    const defaultOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...options,
    };

    return intl.formatDate(date, defaultOptions);
  }

  static convertDateToString(seconds, nanoseconds, options) {
    const withTimezone = options?.withTimezone ?? false;
    const format = options?.format ?? this.AUDIT_DATE_FORMAT;
    const milliseconds = nanoseconds / 1e6;
    const date = new Date(seconds * 1000 + milliseconds);
    const commonDate = moment(date).format(format);
    if (withTimezone) {
      const timezone = getDefaultTimeZone();
      return moment.tz(commonDate, timezone).format();
    }

    return commonDate;
  }

  /**
   * @description Convert a timestamp to a date in the format YYYY-MM-DD
   * @param {number} date - The timestamp to convert
   * @param {string} format - The format to convert the date to
   * @returns {string} The date as string in the format provided (default: YYYY-MM-DD)
   */
  static convertTimestampToDate(date, format = this.DEFAULT_DATE_FORMAT, timezone = null) {
    if (!date) return '-';

    if (date.seconds !== undefined && date.nanoseconds !== undefined) {
      if (timezone) {
        const dateObj = this.toDate(date.seconds, date.nanoseconds);
        return moment.tz(dateObj, timezone).format(format);
      }
      return this.convertDateToString(date.seconds, date.nanoseconds, { format });
    }

    if (date instanceof Date || typeof date === 'string') {
      return moment(date).format(format);
    }

    return '-';
  }

  static formatNumberToMinutes(number) {
    if (!number) return '';
    const duration = moment.duration(number, 'minutes');
    const hours = String(Math.floor(duration.asHours())).padStart(2, '0');
    const minutes = String(duration.minutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  /**
   * @description Convert a date from the dt format (DD/MM/YY) to the standard format (DD/MM/YYYY)
   * @param {string} date - The date to convert in format DD/MM/YY
   * @returns {string} The date as string in the format provided (default: DD/MM/YYYY)
   */
  static dtFormatToStandardFormat(date) {
    return moment(date, this.DEFAULT_DT_DATE_FORMAT, true).format(this.DEFAULT_DATE_FORMAT);
  }

  static standardFormatToDtFormat(date) {
    return moment(date, this.DEFAULT_DATE_FORMAT, true).format(this.DEFAULT_DT_DATE_FORMAT);
  }

  /**
   * Parse a date string against one or more formats in strict mode and emit a
   * single output format. First matching format wins.
   * @param {string} value - Date string to parse
   * @param {string|string[]} formats - Accepted moment format(s)
   * @param {string} outputFormat - Format to emit when valid
   * @returns {string|null} Formatted date, or null when no format matches
   */
  static parseStrict(value, formats, outputFormat) {
    if (!value || typeof value !== 'string' || !outputFormat) return null;

    const parsed = moment(value, formats, true);
    if (!parsed.isValid()) return null;

    return parsed.format(outputFormat);
  }

  /**
   * Returns the numeric difference between two dates using the provided unit.
   * Accepts Date, string, or moment-compatible values.
   * If values are invalid, returns null.
   */
  static getTimeDifference(startDate, endDate, unit = 'hours', precise = true) {
    if (!startDate || !endDate || typeof unit !== 'string') return null;
    const start = moment(startDate);
    const end = moment(endDate);
    if (!start.isValid() || !end.isValid()) return null;
    return end.diff(start, unit as moment.unitOfTime.Diff, precise);
  }
}

DateUtil.setDefaultTimeZone(DateUtil.getDefaultTimeZone());
